import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import type { SettingsData, SettingsItem } from './settings-types'
import type { UserData } from './user-types'
import settingsStructure from '../../mocks/data/settings.json'
import { getUserProfile } from './user'
import type { ConnectionInfo } from './api'

interface UseSettingsLoaderArgs {
  fetchConnections: () => Promise<ConnectionInfo[] | null>
  mergeConnectionState: (settings: SettingsData, connections: ConnectionInfo[]) => SettingsData
}

interface UseSettingsLoaderResult {
  data: SettingsData | null
  setData: React.Dispatch<React.SetStateAction<SettingsData | null>>
  userData: UserData | null
  loading: boolean
  toggles: Record<string, boolean>
  setToggles: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
}

export function useSettingsLoader({ fetchConnections, mergeConnectionState }: UseSettingsLoaderArgs): UseSettingsLoaderResult {
  const { t } = useTranslation('settings')
  const [data, setData] = useState<SettingsData | null>(null)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [toggles, setToggles] = useState<Record<string, boolean>>({})

  useEffect(() => {
    async function loadData(): Promise<void> {
      setLoading(true)
      try {
        const [userResult, liveConnections, appInfo] = await Promise.all([
          getUserProfile(),
          fetchConnections(),
          Capacitor.isNativePlatform() ? App.getInfo().catch(() => null) : Promise.resolve(null),
        ])
        const settings = JSON.parse(JSON.stringify(settingsStructure)) as SettingsData

        // Fall back to mock if Supabase query returns null
        if (!userResult) {
          const { default: mockUser } = await import('../../mocks/data/user.json')
          setUserData(mockUser as UserData)
        } else {
          setUserData(userResult)
        }

        // Patch profile sublabel with real user name/email,
        // and version sublabel with real version/build from the native binary.
        const profileName = userResult?.name
        const profileEmail = userResult?.email
        for (const section of settings.sections) {
          const profileItem = section.items.find((item: SettingsItem) => item.id === 'profile')
          if (profileItem) {
            profileItem.sublabel = profileName && profileName !== 'User' ? profileName : profileEmail ?? ''
          }
          if (appInfo) {
            const versionItem = section.items.find((item: SettingsItem) => item.id === 'version')
            if (versionItem) {
              versionItem.sublabel = `${appInfo.version} (Build ${appInfo.build})`
            }
          }
        }

        // Merge live connection state into settings data (null = backend unreachable, keep mock)
        setData(liveConnections !== null ? mergeConnectionState(settings, liveConnections) : settings)

        // Init toggles from defaults, overriding with localStorage for device-local settings
        const defaults = (settings as SettingsData & { toggleStates?: Record<string, boolean> }).toggleStates ?? {}
        const initialToggles: Record<string, boolean> = {}
        for (const [key, defaultValue] of Object.entries(defaults)) {
          const stored = localStorage.getItem(`setting:${key}`)
          initialToggles[key] = stored !== null ? JSON.parse(stored) : defaultValue
        }
        setToggles(initialToggles)
      } catch (err) {
        console.error('Failed to load settings:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [fetchConnections, mergeConnectionState, t])

  return { data, setData, userData, loading, toggles, setToggles }
}
